import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  onCleanup,
  onMount,
  untrack,
  useContext,
} from 'solid-js';
import { getLogger } from '@trimble/logging';
import { CloudbeesFeatureFlagProvider } from './cloudbees';
import { ConfigFeatureFlagProvider } from './config';
import { ConnectFeatureFlagProvider } from './connect';
import { FeatureFlag } from './featureFlags';
import type {
  FeatureFlagProvidersConfig,
  FeatureFlagSource,
  FeatureFlagsState,
} from './types';

export type FeatureFlagsProviderProps = {
  children: JSX.Element;
  providers: FeatureFlagProvidersConfig;
};

export const FeatureFlagsContext = createContext<FeatureFlagsState>();

export function FeatureFlagsProvider(
  props: FeatureFlagsProviderProps
): JSX.Element {
  const config = untrack(() => props.providers);
  const connect =
    config.connect &&
    new ConnectFeatureFlagProvider(config.connect.helperOptions);
  const cloudbees =
    config.cloudbees &&
    new CloudbeesFeatureFlagProvider({ apiKey: config.cloudbees.apiKey });
  const configValues = createMemo(() => props.providers.config?.values ?? {});
  const configSource =
    config.config && new ConfigFeatureFlagProvider(configValues);
  const sources: FeatureFlagSource[] = [
    connect,
    cloudbees,
    configSource,
  ].filter((source) => source !== undefined);
  const [isReady, setIsReady] = createSignal(false);
  let disposed = false;

  // Declared before onMount so sources receive the initial context before they initialize.
  createEffect(() => {
    connect?.setContext(props.providers.connect?.context ?? {});
    cloudbees?.setContext(props.providers.cloudbees?.context ?? {});
  });

  onMount(async () => {
    const results = await Promise.allSettled(
      sources.map((source) => source.initialize())
    );
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        getLogger().error(
          `Failed to initialize feature flag provider "${sources[index]?.name}":`,
          result.reason
        );
      }
    });
    if (disposed) {
      return;
    }
    setIsReady(true);
  });

  // Visible in dev or with ?log=true. Re-runs when a source's enabled set changes,
  // so the late CloudBees configuration fetch shows up as a second line.
  createEffect(() => {
    if (!isReady()) {
      return;
    }
    sources.forEach((source) => {
      const enabledCount = Object.values(FeatureFlag).filter((flag) =>
        source.hasFlag(flag)
      ).length;
      getLogger().info(
        `[feature-flags] ${source.name} enabled (${enabledCount} flags)`
      );
    });
  });

  onCleanup(() => {
    disposed = true;
    sources.forEach((source) => source.dispose?.());
  });

  const hasFlag = (flag: FeatureFlag): boolean =>
    sources.some((source) => source.hasFlag(flag));

  return (
    <FeatureFlagsContext.Provider value={{ hasFlag, isReady }}>
      {props.children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlags = (): FeatureFlagsState => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    );
  }
  return context;
};
